import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Building2, Users, DollarSign, TrendingUp, TrendingDown,
  Activity, Calendar, Loader2, BarChart3, CreditCard,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts'
import { DateRangeFilter, type DateRange } from '@/components/DateRangeFilter'

interface SeasonRevenueBreakdown {
  totals: {
    gross_cents: number
    new_band_cents: number
    reload_cents: number
    eticket_cents: number
    fees_cents: number
    cash_cents: number
    card_cents: number
    total_orders: number
  }
  per_event: { event_id: number; event_name: string; gross_cents: number; new_band_cents: number; reload_cents: number; eticket_cents: number; fees_cents: number }[]
  credit_allocation: { type: string; label: string; cents: number; transactions: number; unique_bands: number }[]
}

interface SeasonMetrics {
  revenue_total: number
  guests: number
  total_scans: number
  paid_scans: number
  total_hours: number
  avg_revenue_per_event: number
  avg_guests_per_event: number
}

interface EventRow {
  event_id: number
  event_name: string
  start_date: string
  status: string
  revenue: number
  guests: number
  total_scans: number
  hours_open: number
}

interface AttractionRow {
  rank: number
  attraction_name: string
  attraction_type: string
  total_scans: number
  unique_guests: number
  revenue: number
  events_appeared: number
}

interface PlanRow {
  rank: number
  plan_name: string
  units_sold: number
  revenue: number
  events_appeared: number
}

interface SeasonData {
  source: string
  scope: string
  date_from: string
  date_to: string
  events_total: number
  events_with_data: number
  metrics: SeasonMetrics
  per_event: EventRow[]
  top_attractions: AttractionRow[]
  top_plans: PlanRow[]
  generated_at: string
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toLocaleString()}`
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

function YoyBadge({ current, prior }: { current: number; prior: number }) {
  if (!prior) return null
  const pct = ((current - prior) / prior) * 100
  const positive = pct >= 0
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
      positive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
    }`}>
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {positive ? '+' : ''}{pct.toFixed(1)}%
    </span>
  )
}

const CHART_COLORS = ['#0070ab', '#00c7ca', '#ed00be', '#FFC400', '#005b92', '#a6004e', '#00a8c7', '#7c3aed']

const currentYear = new Date().getFullYear().toString()
const priorYear = (new Date().getFullYear() - 1).toString()

export function CompanyDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    preset: 'ytd',
    year: currentYear,
    date_from: `${currentYear}-01-01`,
    date_to: new Date().toISOString().slice(0, 10),
  })

  const [data, setData] = useState<SeasonData | null>(null)
  const [priorData, setPriorData] = useState<SeasonData | null>(null)
  const [revBreakdown, setRevBreakdown] = useState<SeasonRevenueBreakdown | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchSeason = useCallback(async (range: DateRange) => {
    setLoading(true)
    const params = new URLSearchParams()
    if (range.year) params.set('year', range.year)
    if (range.date_from) params.set('date_from', range.date_from)
    if (range.date_to) params.set('date_to', range.date_to)

    const priorParams = new URLSearchParams()
    priorParams.set('year', priorYear)
    if (range.date_from) {
      priorParams.set('date_from', range.date_from.replace(currentYear, priorYear))
      priorParams.set('date_to', (range.date_to || new Date().toISOString().slice(0, 10)).replace(currentYear, priorYear))
    }

    try {
      const [res, priorRes, revRes] = await Promise.all([
        fetch(`/api/dashboard/season?${params}`, { credentials: 'same-origin' }),
        fetch(`/api/dashboard/season?${priorParams}`, { credentials: 'same-origin' }),
        fetch(`/api/dashboard/season/revenue-breakdown?${params}`, { credentials: 'same-origin' }),
      ])
      if (res.ok) setData(await res.json())
      if (priorRes.ok) setPriorData(await priorRes.json())
      if (revRes.ok) setRevBreakdown(await revRes.json())
    } catch { /* non-fatal */ }
    setLoading(false)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => fetchSeason(dateRange), 300)
    return () => clearTimeout(timer)
  }, [dateRange, fetchSeason])

  const m = data?.metrics
  const pm = priorData?.metrics

  const revenueByEvent = useMemo(() => {
    if (!data?.per_event.length) return []
    return data.per_event.slice(0, 12).map(ev => ({
      name: ev.event_name.replace(/\s+\d{4}\s*$/, '').slice(0, 20),
      revenue: ev.revenue,
      guests: ev.guests,
    }))
  }, [data])

  const revenueByType = useMemo(() => {
    if (!data?.top_attractions.length) return []
    const byType: Record<string, number> = {}
    for (const a of data.top_attractions) {
      const t = a.attraction_type || 'Other'
      byType[t] = (byType[t] || 0) + a.revenue
    }
    return Object.entries(byType)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
  }, [data])

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
            <Building2 className="h-5 w-5 text-butler-600" />
          </div>
          <div>
            <h1 className="font-display font-bold text-xl text-ink-dark">Company Dashboard</h1>
            <p className="text-sm text-ink-soft">Organization-wide performance across all events</p>
          </div>
        </div>
      </div>

      <DateRangeFilter value={dateRange} onChange={setDateRange} />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-butler-600" />
        </div>
      )}

      {data && !loading && (
        <>
          {/* KPI Hero */}
          <div className="mm-card-v3 p-6">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-ink-soft">Total Revenue</p>
                <div className="flex items-baseline gap-3 mt-1">
                  <h2 className="font-display font-bold text-3xl text-ink-dark">
                    {m ? fmt(m.revenue_total) : '—'}
                  </h2>
                  {m && pm && <YoyBadge current={m.revenue_total} prior={pm.revenue_total} />}
                </div>
                {pm && (
                  <p className="text-xs text-ink-soft mt-1">
                    Prior year: {fmt(pm.revenue_total)} ({priorYear})
                  </p>
                )}
              </div>
              <div className="text-right text-xs text-ink-soft">
                <div className="mm-data">{data.date_from} → {data.date_to}</div>
                <div>{data.events_with_data} of {data.events_total} events with data</div>
              </div>
            </div>
          </div>

          {/* 6 KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard
              icon={<Users className="h-4 w-4" />}
              label="Total Guests"
              value={m ? fmtNum(m.guests) : '—'}
              prior={pm ? fmtNum(pm.guests) : undefined}
            />
            <KpiCard
              icon={<DollarSign className="h-4 w-4" />}
              label="Avg / Event"
              value={m ? fmt(m.avg_revenue_per_event) : '—'}
              prior={pm ? fmt(pm.avg_revenue_per_event) : undefined}
            />
            <KpiCard
              icon={<Calendar className="h-4 w-4" />}
              label="Events"
              value={`${data.events_with_data}`}
              prior={priorData ? `${priorData.events_with_data}` : undefined}
            />
            <KpiCard
              icon={<Activity className="h-4 w-4" />}
              label="Total Scans"
              value={m ? fmtNum(m.total_scans) : '—'}
            />
            <KpiCard
              icon={<Users className="h-4 w-4" />}
              label="Avg Guests / Event"
              value={m ? fmtNum(m.avg_guests_per_event) : '—'}
              prior={pm ? fmtNum(pm.avg_guests_per_event) : undefined}
            />
            <KpiCard
              icon={<BarChart3 className="h-4 w-4" />}
              label="Active Hours"
              value={m ? fmtNum(m.total_hours) : '—'}
            />
          </div>

          {/* Revenue Composition Strip */}
          {revBreakdown && revBreakdown.totals.gross_cents > 0 && (() => {
            const t = revBreakdown.totals
            const total = t.gross_cents || 1
            const items = [
              { label: 'New Band', cents: t.new_band_cents, color: '#0070ab' },
              { label: 'Reload', cents: t.reload_cents, color: '#00c7ca' },
              { label: 'Online', cents: t.eticket_cents, color: '#ed00be' },
              { label: 'Fees', cents: t.fees_cents, color: '#f59e0b' },
            ].filter(i => i.cents > 0)
            return (
              <section className="mm-card-v3 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="h-4 w-4 text-butler-600" />
                  <h2 className="font-display font-semibold text-sm text-ink-dark">Revenue by Purchase Type</h2>
                </div>
                <div className="flex h-4 rounded-full overflow-hidden mb-3">
                  {items.map(i => (
                    <div key={i.label} style={{ width: `${(i.cents / total) * 100}%`, backgroundColor: i.color }} title={`${i.label}: ${fmt(i.cents)}`} />
                  ))}
                </div>
                <div className="flex items-center gap-5 text-xs text-ink-soft flex-wrap">
                  {items.map(i => (
                    <div key={i.label} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: i.color }} />
                      <span className="font-medium text-ink-dark">{i.label}:</span>
                      <span>{fmt(i.cents)}</span>
                      <span className="text-ink-hint">({((i.cents / total) * 100).toFixed(1)}%)</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5 ml-auto">
                    <span className="text-ink-hint">Cash: {fmt(t.cash_cents)} · Card: {fmt(t.card_cents)}</span>
                  </div>
                </div>
              </section>
            )
          })()}

          {/* Revenue by Event chart + Revenue by Type pie */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <section className="mm-card-v3 p-5 lg:col-span-2">
              <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">Revenue by Event</h2>
              {revenueByEvent.length > 0 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueByEvent} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#8697A8' }} angle={-25} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11, fill: '#8697A8' }} tickFormatter={(v: number) => fmt(v).replace('$', '')} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                        formatter={(value: number) => [fmt(value), 'Revenue']}
                      />
                      <Bar dataKey="revenue" fill="#0070ab" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="py-12 text-center text-xs text-ink-soft">No event data in this range.</div>
              )}
            </section>

            <section className="mm-card-v3 p-5">
              <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">Revenue by Type</h2>
              {revenueByType.length > 0 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={revenueByType}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="45%"
                        outerRadius={80}
                        innerRadius={40}
                        paddingAngle={2}
                      >
                        {revenueByType.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend
                        verticalAlign="bottom"
                        formatter={(value: string) => <span className="text-xs text-ink-soft">{value}</span>}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                        formatter={(value: number) => [fmt(value), 'Revenue']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="py-12 text-center text-xs text-ink-soft">No type data.</div>
              )}
            </section>
          </div>

          {/* Top Attractions + Top Plans side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section className="mm-card-v3 p-5">
              <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">
                Top Attractions — All Events
              </h2>
              <div className="space-y-2">
                {data.top_attractions.slice(0, 10).map((a, i) => {
                  const maxRev = data.top_attractions[0]?.revenue || 1
                  return (
                    <div key={a.attraction_name} className="flex items-center gap-3">
                      <span className="w-5 text-[10px] text-ink-hint text-right font-mono">{i + 1}</span>
                      <span className="w-32 text-xs text-ink-dark shrink-0 truncate font-medium" title={a.attraction_name}>
                        {a.attraction_name}
                      </span>
                      <span className="w-12 text-[10px] text-ink-hint shrink-0">{a.attraction_type}</span>
                      <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.round((a.revenue / maxRev) * 100)}%`,
                            background: 'linear-gradient(90deg, #0070ab, #00c7ca)',
                          }}
                        />
                      </div>
                      <span className="mm-data text-xs font-medium text-ink-dark w-14 text-right">{fmt(a.revenue)}</span>
                      <span className="text-[10px] text-ink-soft w-14 text-right">{fmtNum(a.unique_guests)} guests</span>
                    </div>
                  )
                })}
              </div>
            </section>

            <section className="mm-card-v3 p-5">
              <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">
                Top Plans — All Events
              </h2>
              <div className="space-y-2">
                {data.top_plans.slice(0, 10).map((p, i) => {
                  const maxRev = data.top_plans[0]?.revenue || 1
                  return (
                    <div key={p.plan_name} className="flex items-center gap-3">
                      <span className="w-5 text-[10px] text-ink-hint text-right font-mono">{i + 1}</span>
                      <span className="w-32 text-xs text-ink-soft shrink-0 truncate" title={p.plan_name}>
                        {p.plan_name}
                      </span>
                      <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.round((p.revenue / maxRev) * 100)}%`,
                            background: 'linear-gradient(90deg, #0070ab, #00c7ca)',
                          }}
                        />
                      </div>
                      <span className="mm-data text-xs font-medium text-ink-dark w-14 text-right">{fmt(p.revenue)}</span>
                      <span className="text-[10px] text-ink-soft w-14 text-right">{fmtNum(p.units_sold)} sold</span>
                    </div>
                  )
                })}
              </div>
            </section>
          </div>

          {/* Event performance table */}
          <section className="mm-card-v3 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <h3 className="font-display font-semibold text-sm text-ink-dark">Event Performance</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="py-2.5 px-4 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-soft">Event</th>
                    <th className="py-2.5 px-4 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-soft">Date</th>
                    <th className="py-2.5 px-4 text-right text-[11px] font-semibold uppercase tracking-wide text-ink-soft">Revenue</th>
                    <th className="py-2.5 px-4 text-right text-[11px] font-semibold uppercase tracking-wide text-ink-soft">Guests</th>
                    <th className="py-2.5 px-4 text-right text-[11px] font-semibold uppercase tracking-wide text-ink-soft">Scans</th>
                    <th className="py-2.5 px-4 text-right text-[11px] font-semibold uppercase tracking-wide text-ink-soft">$/Guest</th>
                    <th className="py-2.5 px-4 text-right text-[11px] font-semibold uppercase tracking-wide text-ink-soft">Hours</th>
                    <th className="py-2.5 px-4 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-soft">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_event.map(ev => (
                    <tr key={ev.event_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="py-2.5 px-4 text-sm font-medium text-ink-dark max-w-[200px] truncate">{ev.event_name}</td>
                      <td className="py-2.5 px-4 mm-data text-xs text-ink-soft">{ev.start_date?.slice(0, 10)}</td>
                      <td className="py-2.5 px-4 text-right mm-data text-sm font-medium text-ink-dark">{fmt(ev.revenue)}</td>
                      <td className="py-2.5 px-4 text-right mm-data text-sm text-ink">{fmtNum(ev.guests)}</td>
                      <td className="py-2.5 px-4 text-right mm-data text-xs text-ink-soft">{fmtNum(ev.total_scans)}</td>
                      <td className="py-2.5 px-4 text-right mm-data text-xs text-ink-soft">
                        {ev.guests > 0 ? `$${(ev.revenue / ev.guests).toFixed(2)}` : '—'}
                      </td>
                      <td className="py-2.5 px-4 text-right mm-data text-xs text-ink-soft">{ev.hours_open}</td>
                      <td className="py-2.5 px-4">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          ev.status === 'active' ? 'bg-green-50 text-green-700' :
                          ev.status === 'upcoming' ? 'bg-amber-50 text-amber-700' :
                          'bg-slate-100 text-ink-soft'
                        }`}>
                          {ev.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  )
}

function KpiCard({ icon, label, value, prior }: {
  icon: React.ReactNode; label: string; value: string; prior?: string
}) {
  return (
    <div className="mm-card-v3 p-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-butler-600">{icon}</span>
        <span className="text-[10px] font-medium uppercase tracking-wide text-ink-soft">{label}</span>
      </div>
      <div className="font-display font-bold text-lg text-ink-dark">{value}</div>
      {prior && <p className="text-[10px] text-ink-hint mt-0.5">Prior: {prior}</p>}
    </div>
  )
}
